import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatQualificationParams, qualificationLists } from '../../chat.model';
import { formatPrice } from '../../chat.format';

interface AnswerRow {
  label: string;
  value: string;
}

/**
 * `kind: 'qualification'` (7.2), lado do CRM: as perguntas enviadas e, quando
 * respondidas, as respostas em linhas (tipo, negócio, orçamento, quartos,
 * detalhes). Ações do staff: "Editar" (responder ou corrigir em nome do
 * cliente) e "Procurar imóveis" (abre o envio de imóveis já filtrado).
 */
@Component({
  selector: 'chat-qualification-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-qualification-card.component.html',
  styleUrls: ['./chat-qualification-card.component.css']
})
export class ChatQualificationCardComponent implements OnChanges {
  @Input() params: Partial<ChatQualificationParams> = {};
  @Input() canManage = false;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Output() edit = new EventEmitter<void>();
  @Output() search = new EventEmitter<void>();

  answered = false;
  rows: AnswerRow[] = [];
  fieldLabels: string[] = [];
  /** Pré-visualização das perguntas com as opções que o cliente vê na app. */
  preview: { label: string; options: string[] }[] = [];
  statusText = '';

  ngOnChanges(): void {
    const p = this.params || {};
    const a = p.answers || {};
    this.answered = p.status === 'answered';
    this.fieldLabels = (p.fields || []).map(f => this.strings.qualificationField[f] || f);
    const opts = p.options || { propertyTypes: [], businessTypes: [] };
    this.preview = (p.fields || []).map(f => {
      const label = this.strings.qualificationField[f] || f;
      switch (f) {
        case 'propertyType': return { label, options: (opts.propertyTypes || []).map(o => o.name).slice(0, 6) };
        case 'businessType': return { label, options: (opts.businessTypes || []).map(o => o.name) };
        case 'budget': return { label, options: [this.strings.budgetMinLabel, this.strings.budgetMaxLabel] };
        case 'bedrooms': return { label, options: ['T0', 'T1', 'T2', 'T3', 'T4', 'T5+'] };
        default: return { label, options: [this.strings.qualificationFreeText] };
      }
    });
    this.statusText = this.answered
      ? this.strings.qualificationAnswered.replace('{name}', p.answeredByName || '')
      : this.strings.qualificationPending;
    const rows: AnswerRow[] = [];
    // v8: várias escolhas ("Apartamento ou Moradia"); as respostas antigas trazem só o nome.
    const or = ' ' + this.strings.qualificationOr + ' ';
    const typeNames = Array.isArray(a.propertyTypeNames) && a.propertyTypeNames.length > 0 ? a.propertyTypeNames.join(or) : a.propertyTypeName;
    if (typeNames) {
      rows.push({ label: this.strings.qualificationField['propertyType'], value: typeNames });
    }
    const businessNames = Array.isArray(a.businessTypeNames) && a.businessTypeNames.length > 0 ? a.businessTypeNames.join(or) : a.businessTypeName;
    if (businessNames) {
      rows.push({ label: this.strings.qualificationField['businessType'], value: businessNames });
    }
    const budget = [a.budgetMin, a.budgetMax].filter(v => v !== undefined && v !== null && Number(v) > 0).map(v => formatPrice(Number(v)));
    if (budget.length > 0) {
      rows.push({ label: this.strings.qualificationField['budget'], value: budget.join(' – ') });
    }
    const bedrooms = qualificationLists(a).bedrooms;
    if (bedrooms.length > 0) {
      rows.push({ label: this.strings.qualificationField['bedrooms'], value: bedrooms.map(n => 'T' + n + (n >= 5 ? '+' : '')).join(or) });
    }
    if (a.notes) {
      rows.push({ label: this.strings.qualificationField['notes'], value: a.notes });
    }
    this.rows = rows;
  }
}
